import {cuppa, log} from "../../cuppa/cuppa";
import {GraphQL} from "./GraphQL";
import {CuppaStorage} from "../../cuppa/cuppa.storage";
import {Storages} from "./Storages";
import {Constants} from "./Constants";
import {Utils} from "./Utils";
import {SocketManager} from "./SocketManager";

export class UserCtrl {
	static async auth({activationCode, email, password} = {}){
		return await GraphQL.query({query:`#graphql
            {
                auth(email:"${email}", password:"${password}", activationCode:"${activationCode}", expire:true){
                    accessToken refreshToken
                }
            }
			`, addRootBrackets:false});
	}
	
	static async refreshToken(){
		let lastRefreshTokenTime = CuppaStorage.getDataSync(Storages.lastRefreshTokenTime) || 0;
		let seconds = (Date.now()-parseInt(lastRefreshTokenTime))/1000;
		if(seconds < 60*10) return; // 10 minutes
		CuppaStorage.setDataSync({...Storages.lastRefreshTokenTime, data:Date.now()});
		let token = await CuppaStorage.getData(Storages.auth);
		let result =  await GraphQL.query({mutation:`#graphql
            mutation {
                refreshToken(refreshToken:"${token?.refreshToken}", expire:true){
                    accessToken refreshToken
                }
            }
			`, addRootBrackets:false});
		if(!result?.data?.refreshToken){
			UserCtrl.logOut().then();
			return null;
		}else{
			//CuppaStorage.setData({...Storages.auth, data:result.data.refreshToken}).then();
		}
	}
	
	static async loadUser({id, organizationId}){
		if(!id) return;
		return await GraphQL.query2(
			`#graphql
            query ($organizationId:ID! = ${organizationId}){
                user(id:${id}, organizationId:$organizationId){
                    id, name, last_name, email, mobile, status, date, resource_type, description, super_user, photo,
                    competencies(organizationId: $organizationId){
                        id, organization_id, user_id, question_id, answer_id, rating
                    }
                    organizationUser(organizationId:${organizationId}){
                        id, role_id, parent_user_id, status, push_notification_new_request,
                        role {
                            id, role
                        }
                        parentUser {
                            id, name, last_name
                        }
                    }
                }
            }
			`
		);
	}
	
	static async loadUsers({organizationId = 0, page = 1, quantity = Constants.QUANTITY, condition = '', orderBy = 'date ASC', organizationIds = []}){
		if(!organizationId) return null;
		let query = `#graphql
        {
            users(
                organizationId:${organizationId},
                page:${page},
                quantity:${quantity},
                condition:"${condition}",
                orderBy:"${orderBy}"
                organizationIds:${cuppa.dataToGraphQLInput(organizationIds, {isArray:true}) || null}
            ){
                page, pages, totalRows,
                data{
                    id, name, last_name, email, mobile, status, date, photo
                    activeCases(organizationId: ${organizationId}),
                    organizationUser(organizationId: ${organizationId}){
                        id, organization_id, role_id, parent_user_id, status
                        role {
                            id, role
                        }
                    }
                }
            }
        }
		`
		return await GraphQL.query({query, addRootBrackets: false, printResult: false});
	}
	
	static async getRoles(){
		let roles = await CuppaStorage.getData(Storages.roles);
		if(!roles){
			let result = await GraphQL.query({query:`roles{id, role}`});
			roles = result?.data?.roles;
			if(roles) await CuppaStorage.setData(Storages.roles);
		}
		return roles
	}
	
	static setLoginFormData(data){
		if(data.password) delete data.password;
		CuppaStorage.setData({...Storages.loginFormData, data}).then();
	}
	
	static getUser(){
		return CuppaStorage.getDataSync(Storages.user);
	}
	static getRole({user} = {}){
		if(!user) user = UserCtrl.getUser();
		return user?.organizationUser?.role || {};
	}
	
	static getRoleId({user} = {}){
		return parseInt(UserCtrl.getRole({user})?.id || 0);
	}
	
	static isAdminOrManager(){
		let roleId = UserCtrl.getRoleId();
		return [1,3].includes(roleId);
	}
	
	static isAdmin(){
		let roleId = UserCtrl.getRoleId();
		return [1].includes(roleId);
	}
	
	static isSuperUser({user} = {}){
		if(!user) user = UserCtrl.getUser();
		return cuppa.boolean(user?.super_user);
	}
	
	static async logOut(){
		UserCtrl.deleteRefreshToken();
		SocketManager.disconnect();
		Object.values(Storages).map(storage=>{
			if(storage.name === Storages.loginFormData.name) return;
			CuppaStorage.setData({...storage, data:storage.defaultValue}).then()
		});
		Utils.redirect('/');
	}
	
	static deleteRefreshToken(){
		let token = CuppaStorage.getDataSync(Storages.auth);
		if(!token?.refreshToken) return;
		let mutation = `
			deleteRefreshToken(refreshToken:"${token.refreshToken}")
		`
		GraphQL.query({mutation}).then();
	}
	
	static async setUser({user}){
		let organization = await CuppaStorage.getData({...Storages.organization});
		let types = {
			role:'remove', parent_user:'remove', status:'enum', resource_type:'enum', description:'multiline', rating:'number', super_user:'boolean',
			organizationUser:{push_notification_new_request:'boolean', parentUser:'remove'}
		};
		return await GraphQL.query2(
		`#graphql
        mutation {
            setUser(organizationId:"${organization.id}", user:${cuppa.dataToGraphQLInput(user, {types})}){
                id, name, last_name, email, mobile, status, date, super_user, photo, description
                organizationUser(organizationId: "${organization.id}") {
                    role {
                        id, role
                    }
                }
            }
        }
		`);
	}
	
	static async userExist(organizationId, condition = ''){
		let query = `#graphql
        query($organizationId:ID! = ${organizationId}){
            userInOrganization:users(organizationId:$organizationId, quantity:1, condition:"${condition}"){
                data{
                    id, name, last_name, email, mobile, status, date, resource_type, description
                }
            }
            userGlobally:users(quantity:1, condition:"${condition}"){
                data{
                    id, name, last_name, email, mobile, status, date, resource_type, description
                }
            }
        }
		`
		return GraphQL.query({query, addRootBrackets:false});
	}
	
	static newUserObject(){
		let user = UserCtrl.getUser();
		let object = {
			email:'',
			status:Constants.userStatuses.ACTIVE,
			organizationUser:{role_id:2}
		}
		if(UserCtrl.getRoleId({user}) === 3){
			object.organizationUser.parent_user_id = user.id;
		}
		return object;
	}
	
	static async removeUserFromOrganization(organizationId, userId){
		if(!organizationId || !userId) return;
		let mutation = `
			removeUserFromOrganization(organizationId:${organizationId}, userId:${userId}){
		    id, name
		  }
		`
		return GraphQL.query({mutation});
	}
	
	static canEdit(user){
		const loggedUser = UserCtrl.getUser();
		const loggedUserRole = UserCtrl.getRole();
		if(!user?.id) return true;
		if(UserCtrl.isSuperUser({user:loggedUser})) return true;
		if(parseInt(loggedUserRole?.id) === 1) return true;
		let parent_user_id = user?.parent_user?.id;
		if(parseInt(loggedUserRole?.id) === 3 && (parseInt(parent_user_id) === parseInt(loggedUser.id) || !parent_user_id)) return true;
		if(parseInt(user.id) === parseInt(loggedUser.id)) return true;
		return false;
	}
	
	static async startResetPassword({email, organizationId}){
		let urlParams = {
			hideSignIn:cuppa.boolean(cuppa.urlValue('hideSignIn')),
			mobileApp:cuppa.boolean(cuppa.urlValue('mobileApp')),
		};
		const mutation = `#graphql
        mutation {
            startResetPassword(email:"${email}", organizationId:${organizationId || null}, urlParams: "${cuppa.objectToURL(urlParams)}"){
                id, email
            }
        }
		`
		let result = await GraphQL.query({mutation, addRootBrackets:false});
		return result
	}
	
	static async resetPassword({email, code, password, verifyCodeOnly = false}){
		return await GraphQL.query2(
			`#graphql
				mutation {
                    resetPassword(email:"${email}", code:"${code}", password:"${password}", verifyCodeOnly:${verifyCodeOnly}){
                        id, email
                    }
				}
			`
		);
	}
	
	static async geyKey(id = null, isAnonymous = false){
		if(id && isAnonymous){
			return `anonymous_${id}`;
		}else if(id && !isAnonymous){
			return `user_${id}`;
		}
		
		let user = await UserCtrl.getUser();
		let anonymousUser = null;
		return (user) ? `user_${user?.id}` : `anonymous_${anonymousUser?.id}`;
	}
	
	static pushNotificationNewRequest({user, organization} = {}){
		if(cuppa.boolean(organization?.push_notification_new_request)){
			return true;
		}else{
			return cuppa.boolean(user?.organizationUser?.push_notification_new_request);
		}
	}
}
